import { type JSX, type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';

/**
 * The motion system, ported from the supplied mockup.
 *
 * Content arrives as you reach it, rises a short distance, and settles.
 * Nothing loops, nothing floats, nothing moves while you are trying to read
 * it. The curve is `cubic-bezier(.23, 1, .32, 1)` throughout — fast at the
 * start, settling at the end, which is the difference between "arriving" and
 * "sliding".
 *
 * Reduced motion means NO motion, not less: everything renders in its final
 * position immediately, and the content is identical either way.
 */

/** Matches `prefers-reduced-motion`, and keeps matching if the user changes it. */
export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (event: MediaQueryListEvent): void => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  return reduced;
}

type RevealVariant = 'default' | 'rise' | 'zoom';

interface RevealProps {
  readonly children: ReactNode;
  /** Stagger step. Each index delays the reveal so a row arrives in sequence. */
  readonly index?: number;
  readonly variant?: RevealVariant;
  readonly as?: 'div' | 'section' | 'li' | 'article' | 'h1' | 'h2' | 'p';
  readonly className?: string;
  /** Milliseconds between staggered siblings: 90 by default, 70 in a card row. */
  readonly step?: number;
}

/**
 * Reveals its children when they first scroll into view.
 *
 * IntersectionObserver rather than a scroll handler: a dozen sections
 * recalculating on every frame is exactly the jank this page avoids. It
 * reveals once — re-animating on the way back up makes scrolling restless.
 *
 * The element is always in the DOM; only opacity and transform change. Screen
 * readers and crawlers see the whole page regardless of scroll position.
 */
export function Reveal({
  children,
  index = 0,
  variant = 'default',
  as = 'div',
  className,
  step = 90,
}: RevealProps): JSX.Element {
  const reduced = usePrefersReducedMotion();
  const ref = useRef<HTMLElement | null>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (reduced) {
      setShown(true);
      return;
    }

    const node = ref.current;
    if (node === null || typeof IntersectionObserver !== 'function') {
      setShown(true);
      return;
    }

    let revealed = false;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          reveal();
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.02 },
    );

    const reveal = (): void => {
      if (revealed) return;
      revealed = true;
      setShown(true);
      observer.disconnect();
      window.removeEventListener('scroll', rescue);
    };

    /*
     * Failsafe.
     *
     * An element clipped by an `overflow: hidden` ancestor never reports an
     * intersection, so it never gets `--in`, so it keeps its offset — which can
     * keep it clipped. The content is then invisible for ever, and the failure
     * is self-reinforcing. This page has several sticky, clipped sections, so
     * the guard matters: a motion wrapper must never be the reason content
     * cannot be read.
     *
     * The guard asks a geometric question rather than waiting on a clock: has
     * this element's top reached the middle of the viewport while the observer
     * has still said nothing? Only clipping produces that, so only clipping is
     * rescued. An element that is merely further down the page is left to the
     * observer.
     *
     * This used to be a flat 1.2s timer, which revealed the whole page —
     * every section below the fold arrived in its final state before the
     * reader ever scrolled to it, so the reveal was real but nobody could see
     * it. Half a viewport is deliberately later than the observer's own
     * trigger (a 10% bottom margin), so in the normal case the observer always
     * wins and the timing of the animation is unchanged.
     */
    const rescue = (): void => {
      const rect = node.getBoundingClientRect();
      // No box at all: it can never scroll into anything, so nothing else will
      // ever rescue it.
      if (rect.width === 0 || rect.height === 0) {
        reveal();
        return;
      }
      if (rect.top < window.innerHeight * 0.5) reveal();
    };

    observer.observe(node);
    window.addEventListener('scroll', rescue, { passive: true });
    // Catches a zero-box element, and anything already past the halfway line
    // on first paint.
    rescue();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', rescue);
    };
  }, [reduced]);

  const Tag = as;
  const classes = [
    'jt-reveal',
    variant !== 'default' ? `jt-reveal--${variant}` : '',
    shown ? 'jt-reveal--in' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Tag
      ref={ref as never}
      className={classes}
      style={!reduced && index > 0 ? { transitionDelay: `${index * step}ms` } : undefined}
    >
      {children}
    </Tag>
  );
}

/**
 * Hides the header when scrolling down, brings it back when scrolling up.
 *
 * The mockup's behaviour: chrome gets out of the way while you read, and
 * returns the moment you look for it. Held visible below 120px so it never
 * flickers at the top of the page.
 */
export function useHeaderVisibility(): { readonly hidden: boolean; readonly raised: boolean } {
  const [hidden, setHidden] = useState(false);
  const [raised, setRaised] = useState(false);
  const lastY = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const onScroll = (): void => {
      const y = window.scrollY;
      if (y > 120 && y > lastY.current + 4) setHidden(true);
      else if (y < lastY.current - 4 || y <= 120) setHidden(false);
      setRaised(y > 20);
      lastY.current = y;
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return { hidden, raised };
}

/**
 * Progress from 0 to 1 over the first `distance` pixels of scroll.
 *
 * Drives the hero band growing upward over the title and zooming in. The
 * section turns this into a margin, a height and a scale — all cheap,
 * compositor-friendly properties.
 */
export function useHeroScrollProgress(distance = 380): number {
  const reduced = usePrefersReducedMotion();
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (reduced || typeof window === 'undefined') return;

    const onScroll = (): void => setProgress(Math.min(1, Math.max(0, window.scrollY / distance)));

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [distance, reduced]);

  return progress;
}

/**
 * Parallax offset for a full-bleed band, in pixels.
 *
 * Deliberately gentle. Heavy parallax on a healthcare page reads as a gimmick,
 * and for some people it is genuinely nauseating — which is also why it is
 * disabled entirely under reduced motion.
 */
export function useParallax(ref: RefObject<HTMLElement | null>): number {
  const reduced = usePrefersReducedMotion();
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (reduced || typeof window === 'undefined') return;

    const onScroll = (): void => {
      const node = ref.current;
      if (node === null) return;
      const rect = node.getBoundingClientRect();
      const mid = rect.top + rect.height / 2 - window.innerHeight / 2;
      setOffset(mid * -0.12);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [ref, reduced]);

  return offset;
}

/**
 * Advances an index on a timer, restarting the dwell whenever it is set by hand.
 *
 * Does not run at all under reduced motion. An auto-rotating panel is exactly
 * the unrequested movement that setting exists to stop, and a tab strip that
 * changes under someone's cursor is hostile.
 */
export function useAutoAdvance(
  count: number,
  intervalMs: number,
): { readonly index: number; readonly cycle: number; readonly select: (next: number) => void } {
  const reduced = usePrefersReducedMotion();
  const [index, setIndex] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    if (reduced || count <= 1) return;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % count);
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [count, intervalMs, reduced, cycle]);

  return {
    // Kept in range when the number of items shrinks under it.
    index: count > 0 ? index % count : 0,
    // Exposed so a progress indicator can restart with the dwell.
    cycle,
    // Bumping `cycle` restarts the interval, so a manual choice gets the full
    // dwell rather than whatever was left of the previous one.
    select: (next: number) => {
      setIndex(next);
      setCycle((c) => c + 1);
    },
  };
}

export interface CarouselState {
  /** Nothing to the left: the "previous" arrow takes its resting shade. */
  readonly atStart: boolean;
  /** Nothing to the right. */
  readonly atEnd: boolean;
  readonly step: (direction: -1 | 1) => void;
  /** Wire to the track, so hovering or focusing inside it holds the rotation. */
  readonly hold: {
    readonly onMouseEnter: () => void;
    readonly onMouseLeave: () => void;
    readonly onFocus: () => void;
    readonly onBlur: () => void;
  };
}

/**
 * A native horizontal scroller that advances itself, as the mockup's three
 * carousels do: one card every `intervalMs`, smoothly back to the start at the
 * end, holding still while the pointer or keyboard focus is inside it.
 *
 * The page stays scrollable by hand throughout — this only ever calls
 * `scrollBy`/`scrollTo` on the track, so swiping, the trackpad and the arrow
 * keys all keep working, and the end state is read from the real scroll
 * position rather than an index that could disagree with it.
 *
 * The timer is never left running for nobody. It does not start under reduced
 * motion, it is skipped while the tab is hidden or the carousel is off screen,
 * it does nothing when everything already fits (one card has nowhere to go),
 * and it is cleared on unmount.
 */
export function useCarousel(
  ref: RefObject<HTMLElement | null>,
  options: {
    /** Pixels per automatic step, or a function of the track (e.g. one card). */
    readonly autoStep: number | ((track: HTMLElement) => number);
    /** Pixels per arrow press. Defaults to the automatic step. */
    readonly manualStep?: number | ((track: HTMLElement) => number);
    readonly intervalMs?: number;
  },
): CarouselState {
  const reduced = usePrefersReducedMotion();
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const held = useRef(false);
  const visible = useRef(true);
  const { autoStep, manualStep, intervalMs = 3500 } = options;

  const measure = (step: number | ((track: HTMLElement) => number), track: HTMLElement): number =>
    typeof step === 'number' ? step : step(track);

  // Track the ends from the real scroll position.
  useEffect(() => {
    const track = ref.current;
    if (track === null) return;

    let frame = 0;
    const read = (): void => {
      frame = 0;
      setAtStart(track.scrollLeft <= 4);
      setAtEnd(track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
    };
    const onScroll = (): void => {
      if (frame === 0) frame = window.requestAnimationFrame(read);
    };

    read();
    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      if (frame !== 0) window.cancelAnimationFrame(frame);
      track.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [ref]);

  // Off screen means not advancing.
  useEffect(() => {
    const track = ref.current;
    if (track === null || typeof IntersectionObserver !== 'function') return;
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) visible.current = entry.isIntersecting;
    });
    observer.observe(track);
    return () => observer.disconnect();
  }, [ref]);

  useEffect(() => {
    if (reduced) return;

    const timer = window.setInterval(() => {
      const track = ref.current;
      if (track === null || held.current || !visible.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      // Everything fits: there is nowhere to go and nothing to animate.
      if (track.scrollWidth <= track.clientWidth + 4) return;

      if (track.scrollLeft + track.clientWidth >= track.scrollWidth - 4) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: measure(autoStep, track), behavior: 'smooth' });
      }
    }, intervalMs);

    return () => window.clearInterval(timer);
    // The steps are fixed per section, so the timer is not rebuilt for them.
  }, [ref, reduced, intervalMs]);

  return {
    atStart,
    atEnd,
    step: (direction) => {
      const track = ref.current;
      if (track === null) return;
      track.scrollBy({ left: direction * measure(manualStep ?? autoStep, track), behavior: 'smooth' });
    },
    hold: {
      onMouseEnter: () => {
        held.current = true;
      },
      onMouseLeave: () => {
        held.current = false;
      },
      onFocus: () => {
        held.current = true;
      },
      onBlur: () => {
        held.current = false;
      },
    },
  };
}

/**
 * The width of an element, kept current as it resizes.
 *
 * Used to scale a fixed-size composition (the 1600px Pharmacy Delivery screen)
 * into whatever frame the layout gives it.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = ref.current;
    if (node === null) return;
    setWidth(node.getBoundingClientRect().width);
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) setWidth(entry.contentRect.width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return width;
}

/** True once the element has come within `margin` of the viewport. Never resets. */
export function useNearViewport(ref: RefObject<HTMLElement | null>, margin = '400px'): boolean {
  const [near, setNear] = useState(false);

  useEffect(() => {
    if (near) return;
    const node = ref.current;
    if (node === null || typeof IntersectionObserver !== 'function') {
      setNear(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: margin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref, margin, near]);

  return near;
}
